/**
 * 수강권 일시정지 API (rappo `feat/membership-pause`).
 * - PT   : v3/memberships/:membershipId/pauses…
 * - 그룹 : v1/centers/:centerId/member-tickets/:memberTicketId/pauses…
 * 본문에는 계약 키만 싣는다 — 서버 검증은 모르는 키를 400 으로 거절한다.
 */
import apiClient, { safeJson } from '@/api/client';
import { ChangePauseEndBody, MembershipPauseState, OpenPauseBody, PausePassRef, PauseWriteResponse } from './types';
import { normalizePauseApiError } from './errors';

/** 정지 쓰기 공통 — 오류는 정규화해서 던진다(토큰 갱신 뒤 raw HTTPError 포함). */
const withNormalizedError = async <T,>(run: () => Promise<T>): Promise<T> => {
    try {
        return await run();
    } catch (error) {
        throw await normalizePauseApiError(error);
    }
};

const basePath = (ref: PausePassRef) =>
    ref.kind === 'pt'
        ? `v3/memberships/${encodeURIComponent(ref.membershipId)}/pauses`
        : `v1/centers/${encodeURIComponent(ref.centerId)}/member-tickets/${encodeURIComponent(ref.memberTicketId)}/pauses`;

const toWriteResponse = (data: any): PauseWriteResponse => ({
    success: data?.success === true,
    replayed: data?.replayed === true,
    operation: data?.operation ?? null,
    pause: data?.pause ?? null,
    expiry: data?.expirationDate ?? data?.expiryDate ?? null,
});

export const openPause = async (ref: PausePassRef, body: OpenPauseBody): Promise<PauseWriteResponse> => {
    const res = await withNormalizedError(() => apiClient.post(basePath(ref), {
        json: {
            startDate: body.startDate,
            endDate: body.endDate,
            expectedRevision: body.expectedRevision,
            requestKey: body.requestKey,
        },
    }));
    return toWriteResponse(await safeJson(res));
};

export const resumePause = async (ref: PausePassRef, pauseId: string, requestKey: string): Promise<PauseWriteResponse> => {
    const res = await withNormalizedError(() => apiClient.post(`${basePath(ref)}/${encodeURIComponent(pauseId)}/resume`, {
        json: { requestKey },
    }));
    return toWriteResponse(await safeJson(res));
};

export const cancelPause = async (ref: PausePassRef, pauseId: string, requestKey: string): Promise<PauseWriteResponse> => {
    const res = await withNormalizedError(() => apiClient.post(`${basePath(ref)}/${encodeURIComponent(pauseId)}/cancel`, {
        json: { requestKey },
    }));
    return toWriteResponse(await safeJson(res));
};

export const changePauseEnd = async (
    ref: PausePassRef,
    pauseId: string,
    body: ChangePauseEndBody,
): Promise<PauseWriteResponse> => {
    const res = await withNormalizedError(() => apiClient.patch(`${basePath(ref)}/${encodeURIComponent(pauseId)}`, {
        json: {
            endDate: body.endDate,
            expectedRevision: body.expectedRevision,
            requestKey: body.requestKey,
        },
    }));
    return toWriteResponse(await safeJson(res));
};

const PROBE_ID = '000000000000000000000000';

/**
 * 정지 API 가 있는 서버인지 묻는다.
 *
 * 그룹 끝나는 날 변경 경로는 인증 다음 바로 입력 검증이 돈다. 빈 본문을 보내면
 * 신서버는 **400**(필수 키 없음), 정지 API 가 없는 서버(구서버·롤백 빌드)는 경로가 없어 **404**.
 * 검증 단계에서 끝나 어떤 문서도 읽거나 쓰지 않는다.
 *
 * @returns true = 지원, false = 미지원(404) / 그 밖은 판단 불가로 던진다(호출부가 미지원으로 본다)
 */
export const probePauseSupport = async (centerId: string): Promise<boolean> => {
    let status = 0;
    try {
        const res = await apiClient.patch(
            `v1/centers/${encodeURIComponent(centerId)}/member-tickets/${PROBE_ID}/pauses/${PROBE_ID}`,
            { json: {}, throwHttpErrors: false } as any,
        );
        status = res.status;
    } catch (error: any) {
        // 401 → 토큰 갱신 후 재요청은 ky 기본 옵션으로 나가 400/404 도 던진다(sharedMembership/api.ts 와 같은 사정).
        status = Number(error?.status ?? error?.response?.status ?? 0);
        if (status !== 400 && status !== 404) throw error;
    }
    if (status === 400) return true;
    if (status === 404) return false;
    throw new Error(`pause support probe: unexpected status ${status}`);
};

/**
 * 수강권 한 장의 지금 정지 상태만 다시 읽는다(상세 조회의 `membership.pause`).
 * 생성 관문이 충돌 뒤 판정을 최신으로 맞출 때 쓴다 — 생성 화면의 회원 목록을 다시 불러오지 않기 위해서다
 * (목록을 다시 불러오면 고른 수강권 순서·직원이 정한 종료 시간이 바뀐다, 교차검증 12).
 * 정지 필드가 없는 수강권은 `{ openId: null }`.
 */
export const getLatestPause = async (ref: PausePassRef) => {
    const path =
        ref.kind === 'pt'
            ? `v2/memberships/${encodeURIComponent(ref.membershipId)}`
            : `v1/centers/${encodeURIComponent(ref.centerId)}/member-tickets/${encodeURIComponent(ref.memberTicketId)}/detail`;
    const res = await withNormalizedError(() => apiClient.get(path));
    const data: any = await safeJson(res);
    if (!data?.membership) throw new Error('membership detail missing');
    return (data.membership.pause ?? { openId: null }) as MembershipPauseState;
};
